import {noop} from '@benchling/js-utils';
import assert from 'assert';
import {Spec} from 'immutability-helper';
import React, {useCallback, useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import {InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {
  NamedTableOperation,
  TableOpFunction,
  TableOpOptions,
} from 'notebook/tables/excess/ops/TableOpConstants';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import TableUtil from 'notebook/tables/TableUtil';
import {Note} from 'notebook/util/NoteUtilConstants';

export interface TableOpContextInterface {
  performTableOp: ({tableName, op, options}: NamedTableOperation) => void;
  deleteTableRowsNonContiguous: (tableName: string, ranges: Array<[number, number]>) => void;
  deleteTableRows: (tableName: string, start: number, end: number) => void;
  deleteTableColumns: (tableName: string, start: number, end: number) => void;
  insertTableRowsAbove: (tableName: string, start: number, end: number) => void;
  insertTableRowsBelow: (tableName: string, start: number, end: number) => void;
  reorderTableRowsOrColumns: (tableName: string, options: ReorderOptions) => void;
  insertTableColumnsLeft: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  insertTableColumnsRight: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  renameTable: (tableName: string, newTableName: string) => void;
  mergeTableRegion: (tableName: string, selectedBox: MultiSelectionBox) => void;
  unmergeTableRegion: (tableName: string, selectedBox: MultiSelectionBox) => void;
  mergeIntoSingleInput: (tableName: string, startIndex: number, endIndex: number) => void;
  unmergeSingleInput: (tableName: string, startIndex: number, endIndex: number) => void;
}

export const TableOpContext = makeReactContext<TableOpContextInterface>(() => {
  assert(false, 'TableOpContext must be used within a TableOpContextProvider');
}, 'NoteContext');

/**
 * This is intended to provide all table operations when used within a withSpreadsheetEvaluation wrapper.
 */
export function TableOpContextProvider<TRoot, TItem>({
  spreadsheetTypes,
  fileId,
  getComputedTables,
  getItems,
  getRoot,
  getType,
  getCid,
  getContent,
  setContent,
  requestSelection,
  updateRoot,
  itemsAccessor,
  children,
}: {
  spreadsheetTypes: Array<string>;
  fileId: string | null;
  getComputedTables: () => OutputTableMap;
  getItems: (root: TRoot) => Array<TItem>;
  getRoot: () => TRoot;
  getType: (item: TItem) => string;
  getCid: (item: TItem) => string;
  getContent: (item: TItem) => {table: Table};
  setContent: (type: string, content: object) => Spec<TItem>;
  requestSelection: (selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex>) => void;
  updateRoot: (
    root: TRoot,
    opts?: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    },
    updatedRelatedDocuments?: {[documentId: string]: Note}
  ) => void;
  itemsAccessor: (op: {[i: number]: Spec<TItem>}) => Spec<TRoot>;
  children: React.ReactNode;
}): JSX.Element {
  const performTableOp = useCallback(
    ({tableName, op, options, targetItemContent}: NamedTableOperation) => {
      SpreadsheetEvalUtil.performTableOp({
        tableName,
        fileId,
        op,
        options,
        targetItemContent,
        spreadsheetTypes,
        computedTables: getComputedTables(),
        getItems,
        getRoot,
        getType,
        getCid,
        getContent,
        setContent,
        requestSelection,
        updateRoot,
        itemsAccessor,
      });
    },
    [
      spreadsheetTypes,
      fileId,
      getComputedTables,
      getItems,
      getRoot,
      getType,
      getCid,
      getContent,
      setContent,
      requestSelection,
      updateRoot,
      itemsAccessor,
    ]
  );

  const deleteTableRowsNonContiguous = useCallback(
    (tableName: string, ranges: Array<[number, number]>): void => {
      if (assertAndContinue(ranges.length > 0, 'Cannot delete rows with empty ranges')) {
        performTableOp({
          tableName,
          op: (table, documentName) => excess.ops.deleteRowsNonContiguous(table, documentName, ranges),
          options: {forRowRemoval: true, remakeSingleInputs: true},
        });
      }
    },
    [performTableOp]
  );

  const deleteTableRows = useCallback(
    (tableName: string, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        tableName,
        op: (table, documentName) => excess.ops.deleteRows(table, documentName, start, end),
        options: {forRowRemoval: true, remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const deleteTableColumns = useCallback(
    (tableName: string, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        tableName,
        op: (table, documentName) => excess.ops.deleteColumns(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
    },
    [performTableOp]
  );

  const insertTableRowsAbove = useCallback(
    (tableName: string, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        tableName,
        op: (table, documentName) => excess.ops.insertRowsAbove(table, documentName, start, end),
        options: {remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const insertTableRowsBelow = useCallback(
    (tableName: string, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        tableName,
        op: (table, documentName) => excess.ops.insertRowsBelow(table, documentName, start, end),
        options: {remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const reorderTableRowsOrColumns = useCallback(
    (tableName: string, options: ReorderOptions): void => {
      performTableOp({
        tableName,
        op: (table, documentName) => excess.ops.reorderRowsOrColumns(table, documentName, options),
        options: {remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const insertTableColumnsLeft = useCallback(
    (
      tableName: string,
      start: number,
      end: number,
      columnInfo: InsertableColumnInfo | {} = {},
      initialCellValues?: Array<InitialCellValue>
    ): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        tableName,
        op: (table, documentName) =>
          excess.ops.insertColumnsLeft(table, documentName, start, end, columnInfo, initialCellValues),
        options: {forColumnEdit: true, remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const insertTableColumnsRight = useCallback(
    (
      tableName: string,
      start: number,
      end: number,
      columnInfo: InsertableColumnInfo | {} = {},
      initialCellValues?: Array<InitialCellValue>
    ): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        tableName,
        op: (table, documentName) =>
          excess.ops.insertColumnsRight(table, documentName, start, end, columnInfo, initialCellValues),
        options: {forColumnEdit: true, remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const renameTable = useCallback(
    (tableName: string, newTableName: string): void => {
      performTableOp({
        tableName,
        op: (table, documentName, evaluatedTable, tableMap) =>
          excess.ops.renameTable(tableMap, documentName, table.name, newTableName),
      });
    },
    [performTableOp]
  );

  const mergeTableRegion = useCallback(
    (tableName: string, selectedBox: MultiSelectionBox): void => {
      performTableOp({
        tableName,
        op: (table) => excess.ops.mergeRegion(table, selectedBox),
      });
    },
    [performTableOp]
  );

  const unmergeTableRegion = useCallback(
    (tableName: string, selectedBox: MultiSelectionBox): void => {
      performTableOp({
        tableName,
        op: (table, documentName, evaluatedTable) =>
          excess.ops.unmergeRegion(table, evaluatedTable, selectedBox),
      });
    },
    [performTableOp]
  );

  const mergeIntoSingleInput = useCallback(
    (tableName: string, startIndex: number, endIndex: number): void => {
      performTableOp({
        tableName,
        op: (table, documentName, evaluatedTable) =>
          excess.ops.mergeIntoSingleInput(table, documentName, evaluatedTable, startIndex, endIndex),
      });
    },
    [performTableOp]
  );

  const unmergeSingleInput = useCallback(
    (tableName: string, startIndex: number, endIndex: number): void => {
      performTableOp({
        tableName,
        op: (table, documentName, evaluatedTable) =>
          excess.ops.unmergeSingleInput(table, documentName, evaluatedTable, startIndex, endIndex),
        options: {remakeSingleInputs: false},
      });
    },
    [performTableOp]
  );

  const contextValue: TableOpContextInterface = useMemo(
    () => ({
      performTableOp,
      deleteTableRowsNonContiguous,
      deleteTableRows,
      deleteTableColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      mergeTableRegion,
      unmergeTableRegion,
      mergeIntoSingleInput,
      unmergeSingleInput,
    }),
    [
      performTableOp,
      deleteTableRowsNonContiguous,
      deleteTableRows,
      deleteTableColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      mergeTableRegion,
      unmergeTableRegion,
      mergeIntoSingleInput,
      unmergeSingleInput,
    ]
  );
  return <TableOpContext.Provider value={contextValue}>{children}</TableOpContext.Provider>;
}

export const useTableOps = (): TableOpContextInterface => TableOpContext.useContext();

/**
 * This provides all table operations for tables outside of a NoteEditor environment.
 * For example, Structured Tables Outside the Notebook via SimpleTableEditable
 */
export const SimpleTableOpProvider = ({
  cid,
  getTable,
  getComputedTables,
  updateHandler: updateHandlerWithIndex,
  onDeleteColumns,
  onDeleteRows,
  onReorderRowsOrColumns,
  onInsertRowsBelow,
  onInsertRowsAbove,
  allowMergingCells = false,
  allowSingleInputs = false,
  children,
}: {
  cid: string;
  getTable: () => Table;
  getComputedTables: () => OutputTableMap;
  updateHandler: (
    index: number,
    {
      table,
      selection,
    }: {table: Table; selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex> | null}
  ) => void;
  onDeleteColumns?: (
    index: number,
    start: number,
    end: number,
    deleteTableColumns: (index: number, start: number, end: number) => void
  ) => void;
  onDeleteRows?: (
    index: number,
    start: number,
    end: number,
    deleteTableRows: (index: number, start: number, end: number) => void
  ) => void;
  onReorderRowsOrColumns?: (index: number, options: ReorderOptions) => void;
  onInsertRowsBelow?: (index: number, start: number, end: number) => void;
  onInsertRowsAbove?: (index: number, start: number, end: number) => void;
  allowMergingCells?: boolean;
  allowSingleInputs?: boolean;
  children: React.ReactNode;
}): JSX.Element => {
  // SimpleTableOpProvider operates on a single standalone table, which is always at index 0.
  // Wrap the caller's updateHandler so all methods below don't need to repeat the index.
  const updateHandler = useCallback(
    (update: {
      table: Table;
      selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex> | null;
    }) => {
      updateHandlerWithIndex(0, update);
    },
    [updateHandlerWithIndex]
  );
  const performSimpleTableOp = useCallback(
    ({
      op,
      options,
    }: {
      op: TableOpFunction;
      options: TableOpOptions;
    }): {
      selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex> | null;
      table: Table;
    } | null => {
      // Apply the operation to retrieve the new table map, along with the desired selection.
      const oldTable = getTable();
      const computedTables = getComputedTables();
      const oldTables = {[oldTable.name]: oldTable};
      const {tables: newTables, box} = excess.ops.wrapTableOpForSingleDoc(op)(
        oldTables,
        computedTables,
        oldTable.name
      );
      const newTable = newTables[oldTable.name];

      if (TableUtil.isInvalidOpWithWarn(oldTable, newTable, options)) {
        return null;
      }

      const selection = !box
        ? null
        : {
            multiSelectKey: TableUtil.getTableMultiSelectKey(cid),
            data: {
              multiActive: false,
              multiSelIndex: [box.minI, box.minJ] satisfies TableIndex,
              multiStartIndex: [box.minI, box.minJ] satisfies TableIndex,
              multiEndIndex: [box.maxI, box.maxJ] satisfies TableIndex,
              multiActualIndex: [box.minI, box.minJ] satisfies TableIndex,
            },
          };
      return {selection, table: newTable};
    },
    [cid, getTable, getComputedTables]
  );

  const insertTableColumnsLeft = useCallback(
    (_tableName: string, start: number, end: number, columnInfo: InsertableColumnInfo | {} = {}): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) =>
          excess.ops.insertColumnsLeft(table, documentName, start, end, columnInfo),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }

      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const insertTableColumnsRight = useCallback(
    (_tableName: string, start: number, end: number, columnInfo: InsertableColumnInfo | {} = {}): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) =>
          excess.ops.insertColumnsRight(table, documentName, start, end, columnInfo),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const insertTableRowsAbove = useCallback(
    (_tableName: string, start: number, end: number): void => {
      if (onInsertRowsAbove) {
        onInsertRowsAbove(0, start, end);
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.insertRowsAbove(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler, onInsertRowsAbove]
  );

  const insertTableRowsBelow = useCallback(
    (_tableName: string, start: number, end: number): void => {
      if (onInsertRowsBelow) {
        onInsertRowsBelow(0, start, end);
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.insertRowsBelow(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler, onInsertRowsBelow]
  );

  const reorderTableRowsOrColumns = useCallback(
    (_tableName: string, options: ReorderOptions): void => {
      if (onReorderRowsOrColumns) {
        onReorderRowsOrColumns(0, options);
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.reorderRowsOrColumns(table, documentName, options),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler, onReorderRowsOrColumns]
  );

  const deleteTableColumns = useCallback(
    (_tableName: string, start: number, end: number): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.deleteColumns(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const handleDeleteColumns = useCallback(
    (tableName: string, start: number, end: number): void => {
      if (onDeleteColumns) {
        onDeleteColumns(0, start, end, (_index: number, s: number, e: number) => {
          deleteTableColumns(tableName, s, e);
        });
        return;
      }
      deleteTableColumns(tableName, start, end);
    },
    [deleteTableColumns, onDeleteColumns]
  );

  const deleteTableRowsNonContiguous = useCallback(
    (_tableName: string, ranges: Array<[number, number]>): void => {
      if (assertAndContinue(ranges.length > 0, 'Cannot delete rows with empty ranges')) {
        const tableOpResult = performSimpleTableOp({
          op: (table, documentName) => excess.ops.deleteRowsNonContiguous(table, documentName, ranges),
          options: {forRowRemoval: true},
        });

        if (!tableOpResult) {
          return;
        }
        const {selection, table} = tableOpResult;

        updateHandler({table, selection});
      }
    },
    [performSimpleTableOp, updateHandler]
  );

  const deleteTableRows = useCallback(
    (_tableName: string, start: number, end: number): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.deleteRows(table, documentName, start, end),
        options: {forRowRemoval: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;

      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const handleDeleteRows = useCallback(
    (tableName: string, start: number, end: number): void => {
      if (onDeleteRows) {
        onDeleteRows(0, start, end, (_index: number, s: number, e: number) => {
          deleteTableRows(tableName, s, e);
        });
        return;
      }
      deleteTableRows(tableName, start, end);
    },
    [deleteTableRows, onDeleteRows]
  );

  const renameTable = useCallback(
    (_tableName: string, newTableName: string): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName, evaluatedTable, tableMap) =>
          excess.ops.renameTable(tableMap, documentName, table.name, newTableName),
        options: {},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;

      updateHandler({table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const mergeTableRegion = useCallback(
    (_tableName: string, selectedBox: MultiSelectionBox): void => {
      if (!allowMergingCells) {
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table) => excess.ops.mergeRegion(table, selectedBox),
        options: {},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [allowMergingCells, performSimpleTableOp, updateHandler]
  );

  const unmergeTableRegion = useCallback(
    (_tableName: string, selectedBox: MultiSelectionBox): void => {
      if (!allowMergingCells) {
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, _documentName, evaluatedTable) =>
          excess.ops.unmergeRegion(table, evaluatedTable, selectedBox),
        options: {},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [allowMergingCells, performSimpleTableOp, updateHandler]
  );

  const mergeIntoSingleInput = useCallback(
    (_tableName: string, startIndex: number, endIndex: number): void => {
      if (!allowSingleInputs) {
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName, evaluatedTable) =>
          excess.ops.mergeIntoSingleInput(table, documentName, evaluatedTable, startIndex, endIndex),
        options: {},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [allowSingleInputs, performSimpleTableOp, updateHandler]
  );

  const unmergeSingleInput = useCallback(
    (_tableName: string, startIndex: number, endIndex: number): void => {
      if (!allowSingleInputs) {
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName, evaluatedTable) =>
          excess.ops.unmergeSingleInput(table, documentName, evaluatedTable, startIndex, endIndex),
        options: {remakeSingleInputs: false},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler({table, selection});
    },
    [allowSingleInputs, performSimpleTableOp, updateHandler]
  );

  const contextValue: TableOpContextInterface = useMemo(
    () => ({
      performTableOp: noop,
      deleteTableRowsNonContiguous,
      deleteTableRows: handleDeleteRows,
      deleteTableColumns: handleDeleteColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      mergeTableRegion: allowMergingCells ? mergeTableRegion : noop,
      unmergeTableRegion: allowMergingCells ? unmergeTableRegion : noop,
      mergeIntoSingleInput: allowSingleInputs ? mergeIntoSingleInput : noop,
      unmergeSingleInput: allowSingleInputs ? unmergeSingleInput : noop,
    }),
    [
      deleteTableRowsNonContiguous,
      handleDeleteRows,
      handleDeleteColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      allowMergingCells,
      mergeTableRegion,
      unmergeTableRegion,
      allowSingleInputs,
      mergeIntoSingleInput,
      unmergeSingleInput,
    ]
  );
  return <TableOpContext.Provider value={contextValue}>{children}</TableOpContext.Provider>;
};
